import { useState, useRef, useEffect, useCallback, useContext } from 'react';
import './MovieGuessingGame.css';
import { Link } from 'react-router-dom';
import { FaCog, FaFlag } from 'react-icons/fa';
import FooterSelectionScreen from './FooterSelectionScreen';
import { GameSettingsContext, GameSettingsProvider } from '../contexts/GameSettingsContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const IMAGE_PATH_BASE_URL = process.env.REACT_APP_IMAGE_PATH_BASE_URL;

// console.log("API_BASE_URL = ", API_BASE_URL);
// console.log("IMAGE_PATH_BASE_URL = ", IMAGE_PATH_BASE_URL);
const getAbsoluteImageUrl = (relativePath) => {
  if (!relativePath) {
    return "https://placehold.co/1920x1080?text=Image+Not+Found";
  }
  const cleanedRelativePath = relativePath.startsWith('/') ? relativePath.substring(1) : relativePath;
  return `${IMAGE_PATH_BASE_URL}${cleanedRelativePath}`;
};

const shuffleArray = (array) => {
  let currentIndex = array.length, randomIndex;
  while (currentIndex !== 0) {
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex--;
    [array[currentIndex], array[randomIndex]] = [
      array[randomIndex], array[currentIndex]];
  }
  return array;
};

const resetOptionButtonStyles = () => {
  const currentOptionButtons = document.querySelectorAll('.option-button');
  currentOptionButtons.forEach(button => {
    button.classList.remove(
      'correct-option',
      'incorrect-option',
      'revealed-correct-option'
    );
  });
};

export default function MovieGuessingGame() {
  const [userId, setUserId] = useState(null);
  const { timerDuration } = useContext(GameSettingsContext);
  const { isSoundEnabled } = useContext(GameSettingsContext);
  const [currentMovieData, setCurrentMovieData] = useState(null);
  const [nextMovieData, setNextMovieData] = useState(null);
  const [allMovieTitles, setAllMovieTitles] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [nextMoviePreloadedImageIndex, setNextMoviePreloadedImageIndex] = useState(0);
  const [nextMovieOptions, setNextMovieOptions] = useState([]);
  const [nextMovieCorrectOption, setNextMovieCorrectOption] = useState('');

  const [selectedGenre, setSelectedGenre] = useState(null);
  const [selectedYearBegin, setSelectedYearBegin] = useState(null);
  const [selectedYearEnd, setSelectedYearEnd] = useState(null);

  const [selectedImdbVotesBegin, setSelectedImdbVotesBegin] = useState(null);
  const [selectedImdbVotesEnd, setSelectedImdbVotesEnd] = useState(null);
  const [selectedImdbRatingBegin, setSelectedImdbRatingBegin] = useState(null);
  const [selectedImdbRatingEnd, setSelectedImdbRatingEnd] = useState(null);

  const [excludedMovieIds, setExcludedMovieIds] = useState([]);

  const [score, setScore] = useState(0);
  const [showTimerIncrement, setShowTimerIncrement] = useState(false);
  const [scoreIncrement, setScoreIncrement] = useState(0);
  const [showScoreIncrement, setShowScoreIncrement] = useState(false);
  const [timeLeft, setTimeLeft] = useState(timerDuration);
  const [streak, setStreak] = useState(0);
  const scoreIncrementRef = useRef(null)
  const timerIncrementRef = useRef(null)
  const streakCounterRef = useRef(null);
  const [ghostStreak, setGhostStreak] = useState(0);

  const [isLoading, setIsLoading] = useState(true);

  const [showCountdown, setShowCountdown] = useState(false);
  const [countdownNumber, setCountdownNumber] = useState(3);

  const [options, setOptions] = useState([]);
  const [correctOption, setCorrectOption] = useState('');
  const [selectedOption, setSelectedOption] = useState(null);
  const [revealedCorrectOption, setRevealedCorrectOption] = useState(null);

  const [showCorrectOverlay, setShowCorrectOverlay] = useState(false);
  const [showPassOverlay, setShowPass] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [showRoundEndOverlay, setShowRoundEndOverlay] = useState(false);

  const [correctMovies, setCorrectMovies] = useState([]);
  const [missedMovies, setMissedMovies] = useState([]);
  const [highestStreak, setHighestStreak] = useState(0);

  const [showReportOptions, setShowReportOptions] = useState(false);

  const toggleReportOptions = () => {
    setShowReportOptions(!showReportOptions);
  };

  const globalTimerIntervalRef = useRef(null);

  const audioContextRef = useRef(null);
  const soundBuffersRef = useRef({});
  const [isAudioReady, setIsAudioReady] = useState(false);

  useEffect(() => {
    document.title = "Wunner - The Movie Game";
  }, []);

  const getOrCreateUserId = useCallback(() => {
    let storedUserId = localStorage.getItem('movieGuessingGameUserId');
    if (!storedUserId) {
      storedUserId = crypto.randomUUID();
      localStorage.setItem('movieGuessingGameUserId', storedUserId);
      // console.log('New User ID generated:', storedUserId);
    }
    // else {
    //   console.log('Existing User ID retrieved:', storedUserId);
    // }
    return storedUserId;
  }, []);

  useEffect(() => {
    const id = getOrCreateUserId();
    setUserId(id); // Store the userId in component state
    setIsLoading(false);
  }, [getOrCreateUserId]);

  useEffect(() => {
    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();

    const loadSound = async (name, url) => {
      try {
        const response = await fetch(url);
        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);
        soundBuffersRef.current[name] = audioBuffer;
      } catch (error) {
        console.error(`Error loading sound '${name}':`, error);
      }
    };

    const loadAllSounds = async () => {
      await Promise.all([
        loadSound('correct', '/correct-chime.wav'),
        loadSound('incorrect', '/incorrect-chime.wav'),
        loadSound('pass', '/pass-chime.wav'),
        loadSound('roundStart', '/round-start.wav'),
      ]);
      setIsAudioReady(true);
    };

    loadAllSounds();

    return () => {
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close().catch(e => console.error("Error closing AudioContext:", e));
      }
    };
  }, []);

  const playSound = useCallback((audioBuffer) => {
    if (!isAudioReady || !audioBuffer || !audioContextRef.current) {
      return;
    }

    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume().catch(e => console.error("Error resuming AudioContext:", e));
    }

    const source = audioContextRef.current.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(audioContextRef.current.destination);
    source.start(0);
  }, [isAudioReady]);


  const clearGlobalTimer = () => {
    if (globalTimerIntervalRef.current) {
      if (isSoundEnabled)
        playSound(soundBuffersRef.current.pass);

      clearInterval(globalTimerIntervalRef.current);
      globalTimerIntervalRef.current = null;
    }
  };

  const startGlobalTimer = () => {
    clearGlobalTimer();
    setTimeLeft(timerDuration);

    globalTimerIntervalRef.current = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearGlobalTimer();
          setShowRoundEndOverlay(true);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);
  };

  const preloadImage = useCallback((imageUrl, movieTitle = 'unknown movie') => {
    if (!imageUrl) return;

    const img = new Image();
    img.src = imageUrl;
    // img.onload = () => console.log(`Preloaded image for "${movieTitle}": ${imageUrl}`);
    img.onerror = () => console.error(`Failed to preload image for "${movieTitle}": ${imageUrl}`);
  }, []);


  const generateOptions = useCallback(async (correctMovie) => {
    if (!correctMovie || !correctMovie.title || !correctMovie.genres || correctMovie.genres.length === 0 || !correctMovie.release_year) {
      console.warn("Cannot generate options, missing correct movie data (title, genres, or year).");
      return { options: [], correctTitle: '' };
    }

    const correctTitle = correctMovie.title;
    const correctMovieId = correctMovie.id;
    const correctMovieYear = correctMovie.release_year;
    const primaryGenre = correctMovie.genres[0];

    let wrongOptions = [];
    let poolTitles = [];

    try {
      const optionsPoolUrlWithYears = new URL(`${API_BASE_URL}/api/movies/option_pool`);
      optionsPoolUrlWithYears.searchParams.append('genre', primaryGenre);
      optionsPoolUrlWithYears.searchParams.append('year_begin', correctMovieYear - 5);
      optionsPoolUrlWithYears.searchParams.append('year_end', correctMovieYear + 5);
      optionsPoolUrlWithYears.searchParams.append('exclude_id', correctMovieId);
      optionsPoolUrlWithYears.searchParams.append('limit', 50);

      if (selectedImdbVotesBegin !== null) optionsPoolUrlWithYears.searchParams.append('imdb_votes_begin', (selectedImdbVotesBegin / 10) * 8);
      if (selectedImdbVotesEnd !== null) optionsPoolUrlWithYears.searchParams.append('imdb_votes_end', selectedImdbVotesEnd);
      if (selectedImdbRatingBegin !== null) optionsPoolUrlWithYears.searchParams.append('imdb_rating_begin', selectedImdbRatingBegin - 0.7);
      if (selectedImdbRatingEnd !== null) optionsPoolUrlWithYears.searchParams.append('imdb_rating_end', selectedImdbRatingEnd);


      const responseWithYears = await fetch(optionsPoolUrlWithYears.toString());
      if (!responseWithYears.ok) {
        throw new Error(`HTTP error! status: ${responseWithYears.status}`);
      }
      poolTitles = await responseWithYears.json();

      const MIN_OPTIONS_POOL_SIZE = 10;
      if (poolTitles.length < MIN_OPTIONS_POOL_SIZE) {
        // console.warn(`Not enough titles (${poolTitles.length}) in year-filtered pool. Attempting to fetch without year or imdb filters.`);

        const optionsPoolUrlWithoutYears = new URL(`${API_BASE_URL}/api/movies/option_pool`);
        optionsPoolUrlWithoutYears.searchParams.append('genre', primaryGenre);
        optionsPoolUrlWithoutYears.searchParams.append('exclude_id', correctMovieId);
        optionsPoolUrlWithoutYears.searchParams.append('limit', 50);

        const responseWithoutYears = await fetch(optionsPoolUrlWithoutYears.toString());
        if (!responseWithoutYears.ok) {
          throw new Error(`HTTP error! status: ${responseWithoutYears.status}`);
        }
        poolTitles = await responseWithoutYears.json();
      }

    } catch (error) {
      console.error("Error fetching options pool:", error);
    }

    let filteredPool = poolTitles.filter(title => title && title !== correctTitle);

    let shuffledWrongTitles = shuffleArray(filteredPool);
    const numWrongOptionsToPick = Math.min(3, shuffledWrongTitles.length);

    for (let i = 0; i < numWrongOptionsToPick; i++) {
      wrongOptions.push(shuffledWrongTitles[i]);
    }

    const allOptions = shuffleArray([correctTitle, ...wrongOptions]);
    return { options: allOptions, correctTitle: correctTitle };
  }, [selectedImdbVotesBegin, selectedImdbVotesEnd, selectedImdbRatingBegin, selectedImdbRatingEnd]);

  const fetchRandomMovie = useCallback(async (genreFilter = null, yearBegin = null, yearEnd = null,
    imdbVotesBegin = null, imdbVotesEnd = null, imdbRatingBegin = null, imdbRatingEnd = null,
    currentExcludedIds = [], setExcludedMovieIdsCallback) => {
    try {
      let url = `${API_BASE_URL}/api/movies/random`;
      const params = new URLSearchParams();

      let effectiveExcludedIds = currentExcludedIds;
      if (currentExcludedIds && currentExcludedIds.length > 29) { // Changed 49 to 50
        effectiveExcludedIds = currentExcludedIds.slice(-15); // Gets the last 25 elements
        if (setExcludedMovieIdsCallback) {
          setExcludedMovieIdsCallback(currentExcludedIds.slice(-15)); // Also update the callback with the last 25
        }
      }
      // console.log("excludedIds = ", effectiveExcludedIds);

      if (genreFilter) {
        params.append('genre', encodeURIComponent(genreFilter));
      }
      if (yearBegin !== null) {
        params.append('year_begin', yearBegin);
      }
      if (yearEnd !== null) {
        params.append('year_end', yearEnd);
      }
      if (imdbVotesBegin !== null) {
        params.append('imdb_votes_begin', imdbVotesBegin);
      }
      if (imdbVotesEnd !== null) {
        params.append('imdb_votes_end', imdbVotesEnd);
      }
      if (imdbRatingBegin !== null) {
        params.append('imdb_rating_begin', imdbRatingBegin);
      }
      if (imdbRatingEnd !== null) {
        params.append('imdb_rating_end', imdbRatingEnd);
      }

      if (effectiveExcludedIds && effectiveExcludedIds.length > 0) {
        params.append('exclude_ids', JSON.stringify(effectiveExcludedIds));
      }

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await fetch(url);
      if (!response.ok) {
        if (setExcludedMovieIdsCallback) {
          setExcludedMovieIdsCallback([]);
          console.warn("No more movies found with current filters and exclusions. Resetting excluded IDs.");
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const movie = await response.json(); // Keep as const, no modification here

      return movie;
    } catch (error) {
      console.error("Error fetching random movie:", error);
      if (setExcludedMovieIdsCallback) {
        setExcludedMovieIdsCallback([]);
        console.warn("Error fetching movie. Resetting excluded IDs.");
      }
      return null;
    }
  }, []);

  const initialLoadData = useCallback(async () => {
    setIsLoading(true);
    try {
      let url = `${API_BASE_URL}/api/movies/all_titles`;
      const params = new URLSearchParams();
      if (selectedGenre) params.append('genre', encodeURIComponent(selectedGenre));
      if (selectedYearBegin !== null) params.append('year_begin', selectedYearBegin);
      if (selectedYearEnd !== null) params.append('year_end', selectedYearEnd);
      if (selectedImdbVotesBegin !== null) params.append('imdb_votes_begin', selectedImdbVotesBegin);
      if (selectedImdbVotesEnd !== null) params.append('imdb_votes_end', selectedImdbVotesEnd);
      if (selectedImdbRatingBegin !== null) params.append('imdb_rating_begin', selectedImdbRatingBegin);
      if (selectedImdbRatingEnd !== null) params.append('imdb_rating_end', selectedImdbRatingEnd);

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const titlesResponse = await fetch(url);
      if (!titlesResponse.ok) {
        throw new Error(`HTTP error! status: ${titlesResponse.status}`);
      }
      const titles = await titlesResponse.json();
      setAllMovieTitles(titles);
    } catch (error) {
      console.error("Initial data load failed:", error);
      setShowRoundEndOverlay(true);
    } finally {
      setIsLoading(false);
    }
  }, [selectedGenre, selectedYearBegin, selectedYearEnd, selectedImdbVotesBegin, selectedImdbVotesEnd, selectedImdbRatingBegin, selectedImdbRatingEnd]);

  useEffect(() => {
    if (!gameStarted && allMovieTitles.length === 0 && !isLoading) {
      initialLoadData();
    } else if (isLoading && allMovieTitles.length === 0) {
      initialLoadData();
    }
  }, [gameStarted, allMovieTitles.length, isLoading, initialLoadData]);

  useEffect(() => {
    if (gameStarted && currentMovieData) {
      setSelectedOption(null);
      setRevealedCorrectOption(null);
    } else if (gameStarted && !currentMovieData && !isLoading) {
      console.error("Game started but no current movie data. Ending round.");
      clearGlobalTimer();
      setShowRoundEndOverlay(true);
    }
  }, [currentMovieData, gameStarted, isLoading]);

  function getObscurityBonus(imdbVotes) {
    const maxBonus = 75;
    const minVotes = 50_000;
    const maxVotes = 1_000_000;

    const clampedVotes = Math.min(Math.max(imdbVotes, minVotes), maxVotes);
    const obscurityScore = 1 - (Math.log(clampedVotes) - Math.log(minVotes)) / (Math.log(maxVotes) - Math.log(minVotes));
    return Math.round(maxBonus * obscurityScore);
  }

  const logGuess = useCallback(async (data) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/log_guess`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Failed to log guess:', errorData.error || 'Unknown error occurred on server.');
      } else {
        // console.log('Guess logged successfully!');
      }
    } catch (error) {
      console.error('Error sending guess log to backend:', error);
    }
  }, []);

  const reportImage = useCallback(async (data) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/report_content`, { // <--- Changed endpoint
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Failed to report image:', errorData.error || 'Unknown error occurred on server.');
        // You might want to display a user-friendly error message here
        return { success: false, message: errorData.error || 'Failed to report image.' };
      } else {
        // console.log('Image reported successfully!');
        // You might want to display a success message here
        return { success: true, message: 'Image reported successfully!' };
      }
    } catch (error) {
      console.error('Error sending report to backend:', error);
      // You might want to display a user-friendly error message here
      return { success: false, message: 'An unexpected error occurred while reporting.' };
    }
  }, []);

  const loadNextRoundData = useCallback(async (genre, yearBegin, yearEnd, imdbVotesBegin, imdbVotesEnd, imdbRatingBegin, imdbRatingEnd) => {
    resetOptionButtonStyles();
    setCurrentMovieData(nextMovieData);
    setCurrentImageIndex(nextMoviePreloadedImageIndex);
    setOptions(nextMovieOptions);
    setCorrectOption(nextMovieCorrectOption);

    const updatedExcludedIdsForNextFetch = nextMovieData && nextMovieData.id
      ? [...excludedMovieIds, nextMovieData.id]
      : [...excludedMovieIds];
    setExcludedMovieIds(updatedExcludedIdsForNextFetch);


    let newNextMovie = null;
    let attempts = 0;
    const MAX_ATTEMPTS = 5;

    while (attempts < MAX_ATTEMPTS) {
      newNextMovie = await fetchRandomMovie(
        genre, yearBegin, yearEnd,
        imdbVotesBegin, imdbVotesEnd, imdbRatingBegin, imdbRatingEnd,
        updatedExcludedIdsForNextFetch,
        setExcludedMovieIds
      );
      if (newNextMovie && newNextMovie.id !== nextMovieData?.id) {
        break;
      }
      attempts++;
      console.warn(`Attempt ${attempts}: Fetched same movie ID (${newNextMovie?.id}) as current (${nextMovieData?.id}). Retrying...`);
    }

    if (newNextMovie && newNextMovie.image_paths && newNextMovie.image_paths.length > 0) {
      const newNextRandomIndex = 0;
      const { options: newNextOpts, correctTitle: newNextCorrect } = await generateOptions(newNextMovie);

      setNextMovieData(newNextMovie);
      setNextMoviePreloadedImageIndex(newNextRandomIndex);
      setNextMovieOptions(newNextOpts);
      setNextMovieCorrectOption(newNextCorrect);

      const nextImageUrl = getAbsoluteImageUrl(newNextMovie.image_paths[newNextRandomIndex]);
      preloadImage(nextImageUrl, newNextMovie.title);
    } else {
      console.warn("Could not fetch a new 'next' movie or it has no images after multiple attempts. Game might end soon.");
      setNextMovieData(null);
      setNextMoviePreloadedImageIndex(0);
      setNextMovieOptions([]);
      setNextMovieCorrectOption('');
    }
  }, [nextMovieData, nextMoviePreloadedImageIndex, nextMovieOptions, nextMovieCorrectOption, fetchRandomMovie, preloadImage, generateOptions, excludedMovieIds, setExcludedMovieIds]);

  useEffect(() => {
    const timerIncrement = timerIncrementRef.current;
    if (timerIncrement) {
      if (showTimerIncrement) {
        // When showScoreIncrement becomes true, the element is visible
        // Now, add the animation class
        timerIncrement.classList.add('roll-up');
      } else {
        // When showScoreIncrement becomes false, remove the animation class
        // This is also crucial for subsequent animations
        timerIncrement.classList.remove('roll-up');
      }
    }
  }, [showTimerIncrement]);

  useEffect(() => {
    const scoreIncrementCounter = scoreIncrementRef.current;
    if (scoreIncrementCounter) {
      if (showScoreIncrement) {
        // When showScoreIncrement becomes true, the element is visible
        // Now, add the animation class
        scoreIncrementCounter.classList.add('roll-up');
      } else {
        // When showScoreIncrement becomes false, remove the animation class
        // This is also crucial for subsequent animations
        scoreIncrementCounter.classList.remove('roll-up');
      }
    }
  }, [showScoreIncrement]);

  const handleOptionClick = useCallback(async (clickedTitle) => { // Made async
    if (selectedOption !== null || showRoundEndOverlay || !currentMovieData) {
      return;
    }

    setSelectedOption(clickedTitle);

    let isCorrect = false;
    let actualCorrectTitle = currentMovieData.title; // Fallback in case backend call fails

    try {
      const response = await fetch(`${API_BASE_URL}/api/check_guess`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          movieId: currentMovieData.id,
          guessedTitle: clickedTitle,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      isCorrect = result.isCorrect;
      actualCorrectTitle = result.correctTitle; // Get the correct title from backend

    } catch (error) {
      console.error("Error checking guess with backend:", error);
      // Fallback to frontend check if backend call fails or for debugging
      isCorrect = (clickedTitle === currentMovieData.title);
      console.warn("Falling back to frontend correctness check due to backend error.");
    }

    const streakCounter = streakCounterRef.current;
    const scoreIncrementCounter = scoreIncrementRef.current;

    const logData = {
      userId: userId,
      movieId: currentMovieData.id,
      imageFileName: currentMovieData.image_paths[0],
      isCorrect: isCorrect,
      genreFilter: selectedGenre || null,
      yearBeginFilter: selectedYearBegin,
      yearEndFilter: selectedYearEnd,
      imdbVotesBeginFilter: selectedImdbVotesBegin,
      imdbVotesEndFilter: selectedImdbVotesEnd,
      imdbRatingBeginFilter: selectedImdbRatingBegin,
      imdbRatingEndFilter: selectedImdbRatingEnd,
      guessedOption: clickedTitle,
      correctOption: actualCorrectTitle // Use actualCorrectTitle from backend
    };

    logGuess(logData);


    if (isCorrect) {
      if (isSoundEnabled)
        playSound(soundBuffersRef.current.correct);

      setCorrectMovies(prevCorrectMovies => {
        return [...prevCorrectMovies, actualCorrectTitle];
      });

      try {
        const response = await fetch(`${API_BASE_URL}/api/session/update_score`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            streak: streak,
            ghost_streak: ghostStreak,
            imdb_votes: currentMovieData.imdb_votes,
          }),
        });

        if (!response.ok) {
          throw new Error(`Failed to update score: ${response.statusText}`);
        }

        const data = await response.json();
        setScore(data.new_score);
        setScoreIncrement(data.increment);
      } catch (error) {
        console.error("Error updating score:", error);
        // Optionally notify the user in the UI
      }

      setStreak((prevStreak) => {
        const newStreak = prevStreak + 1;
        if (newStreak > 0 && newStreak % 5 === 0) {
          const newTimeLeft = timeLeft + 9;
          setTimeLeft(newTimeLeft);

          setShowTimerIncrement(true);
          setTimeout(() => {
            setShowTimerIncrement(false);
          }, 750);
        }
        setHighestStreak(prevHighestStreak => Math.max(prevHighestStreak, newStreak));
        return newStreak;
      });

      if (ghostStreak < 0)
        setGhostStreak(0);
      else {
        setGhostStreak((prevGhostStreak) => {
          const newGhostStreak = prevGhostStreak + 1;
          return newGhostStreak;
        });
      }

      setShowCorrectOverlay(true);
      setShowPass(false);

      setShowScoreIncrement(true);

      setTimeout(() => {
        setShowScoreIncrement(false);
      }, 750);

      if (streakCounter) {
        streakCounter.classList.add('correct-animation');
        streakCounter.classList.add('roll-up');
      }

    } else {
      if (isSoundEnabled)
        playSound(soundBuffersRef.current.incorrect);
      setShowCorrectOverlay(false);
      setShowPass(true);

      setMissedMovies(prevMissedMovies => {
        return [...prevMissedMovies, actualCorrectTitle];
      });

      setRevealedCorrectOption(actualCorrectTitle); // Use actualCorrectTitle from backend

      if (streakCounter) {
        streakCounter.classList.add('roll-down');
        streakCounter.classList.add('incorrect-animation');
      }
      setTimeout(() => {
        setStreak(0);

        if (ghostStreak > 0)
          setGhostStreak(0);
        else {
          const newGhostStreak = ghostStreak - 1;
          setGhostStreak(newGhostStreak);
        }

        if (streakCounter) {
          streakCounter.classList.remove('roll-down');
          streakCounter.classList.remove('incorrect-animation');
        }
      }, 750);
    }

    // console.log("Ghost Streak: ", ghostStreak);

    setTimeout(() => {
      setShowCorrectOverlay(false);
      setShowPass(false);
      setSelectedOption(null);
      setRevealedCorrectOption(null);

      if (isCorrect && streakCounter) {
        streakCounter.classList.remove('correct-animation');
        streakCounter.classList.remove('roll-up');
      }

      if (nextMovieData) {
        loadNextRoundData(selectedGenre, selectedYearBegin, selectedYearEnd,
          selectedImdbVotesBegin, selectedImdbVotesEnd, selectedImdbRatingBegin, selectedImdbRatingEnd);
      } else {
        console.warn("No next movie preloaded. Attempting to fetch directly or ending round.");
        clearGlobalTimer();
        setShowRoundEndOverlay(true);
      }
    }, 800);
  }, [
    selectedOption,
    showRoundEndOverlay,
    currentMovieData,
    nextMovieData,
    getOrCreateUserId,
    logGuess,
    currentImageIndex,
    selectedGenre,
    selectedYearBegin,
    selectedYearEnd,
    selectedImdbVotesBegin,
    selectedImdbVotesEnd,
    selectedImdbRatingBegin,
    selectedImdbRatingEnd,
    playSound,
    soundBuffersRef,
    setCorrectMovies,
    setMissedMovies,
    setScore,
    setStreak,
    setGhostStreak,
    setHighestStreak,
    loadNextRoundData
  ]);


  const resetGame = async () => {
    resetOptionButtonStyles();
    setShowRoundEndOverlay(false);
    setCorrectMovies([]);
    setMissedMovies([]);
    try {
      const response = await fetch(`${API_BASE_URL}/api/session/reset_score`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to reset score: ${response.statusText}`);
      }

      const data = await response.json();
      setScore(data.score); // should be 0
    } catch (error) {
      console.error("Error resetting score:", error);
      // optionally show a user-friendly error message in your UI
    }
    setStreak(0);
    setGhostStreak(0);
    setHighestStreak(0);
    setShowCorrectOverlay(false);
    setShowPass(false);
    setSelectedOption(null);
    setRevealedCorrectOption(null);
    setOptions([]);
    setCorrectOption('');
    clearGlobalTimer();

    setGameStarted(false);
    setCountdownNumber(3);
    setShowCountdown(true);
    setExcludedMovieIds([]);

    setIsLoading(true);
    let currentMovie = null;
    let nextMovie = null;
    let attempts = 0;
    const MAX_ATTEMPTS = 5;

    let tempExcludedIds = [];

    currentMovie = await fetchRandomMovie(
      selectedGenre, selectedYearBegin, selectedYearEnd,
      selectedImdbVotesBegin, selectedImdbVotesEnd, selectedImdbRatingBegin, selectedImdbRatingEnd,
      tempExcludedIds,
      setExcludedMovieIds
    );

    if (currentMovie && currentMovie.id) {
      tempExcludedIds.push(currentMovie.id);
    }

    while (attempts < MAX_ATTEMPTS) {
      nextMovie = await fetchRandomMovie(
        selectedGenre, selectedYearBegin, selectedYearEnd,
        selectedImdbVotesBegin, selectedImdbVotesEnd, selectedImdbRatingBegin, selectedImdbRatingEnd,
        tempExcludedIds,
        setExcludedMovieIds
      );
      if (nextMovie) {
        if (nextMovie.id !== currentMovie?.id) {
          tempExcludedIds.push(nextMovie.id);
          break;
        } else {
          console.warn(`Attempt ${attempts}: Fetched same movie ID (${nextMovie?.id}) as current (${currentMovie?.id}) despite exclusion. Retrying...`);
          if (!tempExcludedIds.includes(nextMovie.id)) {
            tempExcludedIds.push(nextMovie.id);
          }
        }
      } else {
        console.warn("Current unique movie pool exhausted during reset. Attempting to restart cycle.");
        tempExcludedIds = [];
        nextMovie = await fetchRandomMovie(
          selectedGenre, selectedYearBegin, selectedYearEnd,
          selectedImdbVotesBegin, selectedImdbVotesEnd, selectedImdbRatingBegin, selectedImdbRatingEnd,
          tempExcludedIds,
          setExcludedMovieIds
        );
        if (nextMovie) {
          tempExcludedIds.push(nextMovie.id);
          break;
        } else {
          console.error("Even after resetting exclusions, no movies found matching filters during reset. Ending round.");
          break;
        }
      }
      attempts++;
    }

    setIsLoading(false);

    if (currentMovie && nextMovie && currentMovie.image_paths.length > 0 && nextMovie.image_paths.length > 0) {
      const currentRandomIndex = 0;
      const nextRandomIndex = 0;

      const { options: currentOpts, correctTitle: currentCorrect } = await generateOptions(currentMovie);
      const { options: nextOpts, correctTitle: nextCorrect } = await generateOptions(nextMovie);

      setCurrentMovieData(currentMovie);
      setCurrentImageIndex(currentRandomIndex);
      setNextMovieData(nextMovie);
      setNextMoviePreloadedImageIndex(nextRandomIndex);
      setNextMovieOptions(nextOpts);
      setNextMovieCorrectOption(nextCorrect);

      const currentImageUrl = getAbsoluteImageUrl(currentMovie.image_paths[currentRandomIndex]);
      preloadImage(currentImageUrl, currentMovie.title);

      const nextImageUrl = getAbsoluteImageUrl(nextMovie.image_paths[nextRandomIndex]);
      preloadImage(nextImageUrl, nextMovie.title);

      setExcludedMovieIds(tempExcludedIds);

      setCountdownNumber(3);

      if (isSoundEnabled)
        playSound(soundBuffersRef.current.roundStart);

      let count = 3;
      const countdownInterval = setInterval(() => {
        count--;
        if (count > 0) {
          setCountdownNumber(count);
        } else {
          clearInterval(countdownInterval);
          setShowCountdown(false);
          setGameStarted(true);

          if (isSoundEnabled)
            playSound(soundBuffersRef.current.pass);

          startGlobalTimer();
          setOptions(currentOpts);
          setCorrectOption(currentCorrect);
        }
      }, 1000);
      setCountdownNumber(3);
    } else {
      console.error("Cannot reset game: Not enough movies available or movies have no images with selected genre and/or year.");
      setGameStarted(false);
      setShowRoundEndOverlay(true);
      setShowCountdown(false);
    }
  };

  const handleReportClick = useCallback(async (reportType) => {
    if (window.confirm(`Do you want to report this image?`)) {
      // console.log("Report type clicked:", reportType);

      const reportData = {
        userId: userId,
        movieId: currentMovieData.id,
        // Removed: imageIndex: currentImageIndex, // This is no longer useful as it's always 0
        // NEW: Send the actual filename
        reportedImageFilename: currentMovieData.image_paths[0],
        reportType: reportType
      };

      const result = await reportImage(reportData);

      if (result.success) {
        // console.log(result.message);
        window.alert("Your report has been received. Thank you.");
      } else {
        console.error(result.message);
      }
    }

    setShowReportOptions(false);

  }, [getOrCreateUserId, currentMovieData, currentImageIndex, reportImage]);

  const goToSelectionScreen = async () => {
    resetOptionButtonStyles();
    setShowRoundEndOverlay(false);
    setGameStarted(false);
    setCorrectMovies([]);
    setMissedMovies([]);
    try {
      const response = await fetch(`${API_BASE_URL}/api/session/reset_score`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to reset score: ${response.statusText}`);
      }

      const data = await response.json();
      setScore(data.score); // should be 0
    } catch (error) {
      console.error("Error resetting score:", error);
      // optionally show a user-friendly error message in your UI
    }
    setStreak(0);
    setGhostStreak(0);
    setHighestStreak(0);
    setShowCorrectOverlay(false);
    setShowPass(false);
    setSelectedOption(null);
    setRevealedCorrectOption(null);
    setOptions([]);
    setCorrectOption('');
    clearGlobalTimer();
    setCurrentMovieData(null);
    setNextMovieData(null);
    setNextMoviePreloadedImageIndex(0);
    setNextMovieOptions([]);
    setNextMovieCorrectOption('');
    setAllMovieTitles([]);
    setSelectedGenre(null);
    setSelectedYearBegin(null);
    setSelectedYearEnd(null);
    setSelectedImdbVotesBegin(null);
    setSelectedImdbVotesEnd(null);
    setSelectedImdbRatingBegin(null);
    setSelectedImdbRatingEnd(null);
    setIsLoading(true);
    setShowCountdown(false);
    setExcludedMovieIds([]);
  };

  const startGameSession = async (genreToStartWith, yearBegin = null, yearEnd = null,
    imdbVotesBegin = null, imdbVotesEnd = null, imdbRatingBegin = null, imdbRatingEnd = null) => {
    setSelectedGenre(genreToStartWith);
    setSelectedYearBegin(yearBegin);
    setSelectedYearEnd(yearEnd);
    setSelectedImdbVotesBegin(imdbVotesBegin);
    setSelectedImdbVotesEnd(imdbVotesEnd);
    setSelectedImdbRatingBegin(imdbRatingBegin);
    setSelectedImdbRatingEnd(imdbRatingEnd);

    setIsLoading(true);
    setGameStarted(false);
    setCountdownNumber(3);
    setShowCountdown(true);
    setExcludedMovieIds([]);

    let initialCurrentMovie = null;
    let initialNextMovie = null;
    let attempts = 0;
    const MAX_ATTEMPTS = 5;

    let tempExcludedIds = [];

    initialCurrentMovie = await fetchRandomMovie(
      genreToStartWith, yearBegin, yearEnd,
      imdbVotesBegin, imdbVotesEnd, imdbRatingBegin, imdbRatingEnd,
      tempExcludedIds,
      setExcludedMovieIds
    );

    if (!initialCurrentMovie) {
      console.warn(`No initial current movie found matching filters. Game cannot start.`);
      setGameStarted(false);
      setShowCountdown(false);
      setShowRoundEndOverlay(true);
      setIsLoading(false);
      return;
    }
    tempExcludedIds.push(initialCurrentMovie.id);

    while (attempts < MAX_ATTEMPTS) {
      initialNextMovie = await fetchRandomMovie(
        genreToStartWith, yearBegin, yearEnd,
        imdbVotesBegin, imdbVotesEnd, imdbRatingBegin, imdbRatingEnd,
        tempExcludedIds,
        setExcludedMovieIds
      );

      if (initialNextMovie) {
        if (initialNextMovie.id !== initialCurrentMovie.id) {
          tempExcludedIds.push(initialNextMovie.id);
          break;
        } else {
          console.warn(`Attempt ${attempts}: Fetched same movie ID (${initialNextMovie?.id}) as current (${initialCurrentMovie?.id}) despite exclusion. Retrying for next movie...`);
          if (!tempExcludedIds.includes(initialNextMovie.id)) {
            tempExcludedIds.push(initialNextMovie.id);
          }
        }
      } else {
        console.warn("Initial unique next movie pool exhausted. Attempting to reset exclusions and find a new next movie.");
        tempExcludedIds = [initialCurrentMovie.id];
        initialNextMovie = await fetchRandomMovie(
          genreToStartWith, yearBegin, yearEnd,
          imdbVotesBegin, imdbVotesEnd, imdbRatingBegin, imdbRatingEnd,
          tempExcludedIds,
          setExcludedMovieIds
        );
        if (initialNextMovie) {
          tempExcludedIds.push(initialNextMovie.id);
          break;
        } else {
          console.error("Even after resetting exclusions for next movie, no second movie found matching filters. Game cannot start.");
          break;
        }
      }
      attempts++;
    }

    setIsLoading(false);

    if (!initialCurrentMovie || !initialNextMovie || initialCurrentMovie.image_paths.length === 0 || initialNextMovie.image_paths.length === 0) {
      console.warn(`Not enough movies found or movies have no images for selected filters. Game cannot start.`);
      setGameStarted(false);
      setShowCountdown(false);
      setShowRoundEndOverlay(true);
      return;
    }

    const currentRandomIndex = 0;
    const nextRandomIndex = 0;

    const { options: currentOpts, correctTitle: currentCorrect } = await generateOptions(initialCurrentMovie);
    const { options: nextOpts, correctTitle: nextCorrect } = await generateOptions(initialNextMovie);

    setCurrentMovieData(initialCurrentMovie);
    setCurrentImageIndex(currentRandomIndex);
    setNextMovieData(initialNextMovie);
    setNextMoviePreloadedImageIndex(nextRandomIndex);
    setNextMovieOptions(nextOpts);
    setNextMovieCorrectOption(nextCorrect);

    const initialCurrentImageUrl = getAbsoluteImageUrl(initialCurrentMovie.image_paths[currentRandomIndex]);
    preloadImage(initialCurrentImageUrl, initialCurrentMovie.title);

    const initialNextImageUrl = getAbsoluteImageUrl(initialNextMovie.image_paths[nextRandomIndex]);
    preloadImage(initialNextImageUrl, initialNextMovie.title);

    setCorrectMovies([]);
    setMissedMovies([]);
    try {
      const response = await fetch(`${API_BASE_URL}/api/session/reset_score`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to reset score: ${response.statusText}`);
      }

      const data = await response.json();
      setScore(data.score); // should be 0
    } catch (error) {
      console.error("Error resetting score:", error);
      // optionally show a user-friendly error message in your UI
    }
    setStreak(0);
    setGhostStreak(0);
    setHighestStreak(0);
    setShowCorrectOverlay(false);
    setShowPass(false);
    setShowRoundEndOverlay(false);
    setSelectedOption(null);
    setRevealedCorrectOption(null);
    setOptions([]);
    setCorrectOption('');
    clearGlobalTimer();

    setExcludedMovieIds(tempExcludedIds);

    setCountdownNumber(3);

    if (isSoundEnabled)
      playSound(soundBuffersRef.current.roundStart);

    let count = 3;
    const countdownInterval = setInterval(() => {
      count--;
      if (count > 0) {
        setCountdownNumber(count);
      } else {
        clearInterval(countdownInterval);
        setShowCountdown(false);
        setGameStarted(true);

        if (isSoundEnabled)
          playSound(soundBuffersRef.current.pass);

        startGlobalTimer();
        setOptions(currentOpts);
        setCorrectOption(currentCorrect);
      }
    }, 1000);
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!gameStarted || selectedOption !== null || showRoundEndOverlay || showCorrectOverlay || showPassOverlay) {
        return;
      }

      const key = event.key;
      let optionIndex = -1;

      switch (key) {
        case '1': optionIndex = 0; break;
        case '2': optionIndex = 1; break;
        case '3': optionIndex = 2; break;
        case '4': optionIndex = 3; break;
        case 'a': optionIndex = 0; break;
        case 's': optionIndex = 1; break;
        case 'd': optionIndex = 2; break;
        case 'f': optionIndex = 3; break;
        case 'j': optionIndex = 0; break;
        case 'k': optionIndex = 1; break;
        case 'l': optionIndex = 2; break;
        case ';': optionIndex = 3; break;
        default: return;
      }

      if (optionIndex !== -1 && options[optionIndex]) {
        handleOptionClick(options[optionIndex]);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [gameStarted, selectedOption, showRoundEndOverlay, showCorrectOverlay, showPassOverlay, options, handleOptionClick]);

  const imageSrc = currentMovieData && currentMovieData.image_paths && currentMovieData.image_paths.length > 0
    ? getAbsoluteImageUrl(currentMovieData.image_paths[currentImageIndex])
    : "https://placehold.co/1920x1080?text=Loading";

  return (
    <div className="game-container">
      <div className={`round-end-overlay ${showRoundEndOverlay ? 'show' : ''}`}>
        <div className="round-end-content">
          <h2>ROUND SCORE: {new Intl.NumberFormat().format(Math.round(score))}!</h2>
          <p>Highest Streak: {highestStreak}</p>
          <div className="movie-lists-container">
            <div className="movie-list correct-list">
              <h3>Correct: {correctMovies.length}</h3>
              <ul>
                {correctMovies.map((movie, index) => (
                  <li key={`correct-${index}`}>{movie}</li>
                ))}
              </ul>
            </div>
            <div className="movie-list missed-list">
              <h3>Missed: {missedMovies.length}</h3>
              <ul>
                {missedMovies.map((movie, index) => (
                  <li key={`missed-${index}`}>{movie}</li>
                ))}
                {showRoundEndOverlay && selectedOption !== correctOption && currentMovieData && (
                  <li key={`missed-final-${missedMovies.length}`}>{currentMovieData.title}</li>
                )}
              </ul>
            </div>
          </div>
          <div className="round-end-buttons">
            <button onClick={resetGame} className="start-button">
              START NEW ROUND
            </button>
            <button onClick={goToSelectionScreen} className="start-button">
              CATEGORY SELECT
            </button>
          </div>
        </div>
      </div>

      {gameStarted && !showCountdown && (
        <>
          {timeLeft > 10 ? (
            <div className="timer-container">
              Time: {timeLeft} Seconds
            </div>
          ) : (
            <div className="timer-container-low">
              Time: {timeLeft} {timeLeft === 1 ? ("Second") : ("Seconds")}
            </div>
          )}

          <div className="streak-counter" ref={streakCounterRef}>
            Streak: {streak}
          </div>

          <div className="score-container">
            Score: {Math.round(score)}
          </div>

          <div
            className={`score-increment-container ${showScoreIncrement ? 'show' : ''}`}
            ref={scoreIncrementRef}
          >
            +{Math.round(scoreIncrement)}
          </div>

          <div
            className={`timer-increment-container ${showTimerIncrement ? 'show' : ''}`}
            ref={timerIncrementRef}
          >
            +10
          </div>
        </>
      )}

      <div className="game-card">
        {(gameStarted || showCountdown) && (
          <div className="game-header">
            <img
              src="/logo.png"
              alt="Logo image"
              className="logo-image-game-header"
              onClick={gameStarted ? goToSelectionScreen : undefined} // Or null, to disable click
              style={{ cursor: gameStarted ? 'pointer' : 'default' }}
            />
          </div>
        )}

        <div className="game-content">
          {isLoading && allMovieTitles.length === 0 ? (
            <div className="loading-message">Connecting to server...</div>
          ) : allMovieTitles.length === 0 ? (
            <div className="no-movies-message">
              No movies found. Please check your data source and API.
            </div>
          ) : (
            <>
              {!gameStarted && !showCountdown ? (
                <div className="selection-screen-container">
                  <img
                    src="/logo.png"
                    alt="Game Logo"
                    className="logo-image-start-overlay"
                  />
                  <img
                    src="/logo2.png"
                    alt="Game Logo"
                    className="logo-name"
                  />
                  {/* <h1>Pick a category. Guess the movie!</h1> */}
                  <div className="selection-columns">
                    <button
                      onClick={() => startGameSession(null, null, null, null, null, null, null)}
                      className={`selection-card-button ${selectedGenre === null && selectedYearBegin === null && selectedImdbVotesBegin === null && selectedImdbRatingBegin === null ? 'active' : ''}`}
                    >
                      RANDOM
                    </button>
                    <button
                      onClick={() => startGameSession("Comedy", null, null, null, null, null, null)}
                      className={`selection-card-button ${selectedGenre === "Comedy" && selectedYearBegin === null && selectedImdbVotesBegin === null && selectedImdbRatingBegin === null ? 'active' : ''}`}
                    >
                      COMEDY
                    </button>
                    <button
                      onClick={() => startGameSession("Romance", null, null, null, null, null, null)}
                      className={`selection-card-button ${selectedGenre === "Romance" && selectedYearBegin === null && selectedImdbVotesBegin === null && selectedImdbRatingBegin === null ? 'active' : ''}`}
                    >
                      ROMANCE
                    </button>
                    <button
                      onClick={() => startGameSession("Horror", null, null, null, null, null, null)}
                      className={`selection-card-button ${selectedGenre === "Horror" && selectedYearBegin === null && selectedImdbVotesBegin === null && selectedImdbRatingBegin === null ? 'active' : ''}`}
                    >
                      HORROR
                    </button>
                    <button
                      onClick={() => startGameSession("Sci-Fi", null, null, null, null, null, null)}
                      className={`selection-card-button ${selectedGenre === "Sci-Fi" && selectedYearBegin === null && selectedImdbVotesBegin === null && selectedImdbRatingBegin === null ? 'active' : ''}`}
                    >
                      SCI-FI
                    </button>
                    <button
                      onClick={() => startGameSession("Drama", null, null, null, null, null, null)}
                      className={`selection-card-button ${selectedGenre === "Drama" && selectedYearBegin === null && selectedImdbVotesBegin === null && selectedImdbRatingBegin === null ? 'active' : ''}`}
                    >
                      DRAMA
                    </button>
                    <button
                      onClick={() => startGameSession("Action", null, null, null, null, null, null)}
                      className={`selection-card-button ${selectedGenre === "Action" && selectedYearBegin === null && selectedImdbVotesBegin === null && selectedImdbRatingBegin === null ? 'active' : ''}`}
                    >
                      ACTION
                    </button>
                    <button
                      onClick={() => startGameSession("Adventure", null, null, null, null, null, null)}
                      className={`selection-card-button ${selectedGenre === "Adventure" && selectedYearBegin === null && selectedImdbVotesBegin === null && selectedImdbRatingBegin === null ? 'active' : ''}`}
                    >
                      ADVENTURE
                    </button>

                    <button
                      onClick={() => startGameSession(null, 1990, 1999, null, null, null, null)}
                      className={`selection-card-button ${selectedYearBegin === 1990 && selectedYearEnd === 1999 && selectedGenre === null && selectedImdbVotesBegin === null && selectedImdbRatingBegin === null ? 'active' : ''}`}
                    >
                      90's
                    </button>
                    <button
                      onClick={() => startGameSession(null, 2000, 2009, null, null, null, null)}
                      className={`selection-card-button ${selectedYearBegin === 2000 && selectedYearEnd === 2009 && selectedGenre === null && selectedImdbVotesBegin === null && selectedImdbRatingBegin === null ? 'active' : ''}`}
                    >
                      00's
                    </button>

                    <button
                      onClick={() => startGameSession(null, null, null, 400000, null, null, null)}
                      className={`selection-card-button ${selectedImdbVotesBegin === 400000 && selectedImdbVotesEnd === null && selectedGenre === null && selectedYearBegin === null && selectedImdbRatingBegin === null ? 'active' : ''}`}
                    >
                      BLOCKBUSTERS
                    </button>

                    <button
                      onClick={() => startGameSession(null, null, null, null, null, 7.5, null)}
                      className={`selection-card-button ${selectedImdbRatingBegin === 7.5 && selectedImdbRatingEnd === null && selectedGenre === null && selectedYearBegin === null && selectedImdbVotesBegin === null ? 'active' : ''}`}
                    >
                      ACCLAIMED
                    </button>

                  </div>
                  <Link to="/settings" className="settings-button">
                    <span className="settings-button-text">Settings</span>
                    <FaCog className="settings-button-icon" />
                  </Link>
                  <FooterSelectionScreen />
                </div>
              ) : (
                <>
                  <div className="movie-image-wrapper">
                    {showCountdown ? (
                      <div className="countdown-black-screen">
                        <div className="countdown-number">{countdownNumber}</div>
                      </div>
                    ) : (
                      currentMovieData ? (
                        <img
                          key={currentMovieData.id + '-' + currentImageIndex}
                          src={imageSrc}
                          alt="Movie screenshot"
                          className="movie-image"
                          onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/1920x1080?text=Image+Not+Found"; console.error("Image failed to load, src was:", e.target.src); }}
                        />
                      ) : (
                        <div className="loading-image-placeholder">Loading image...</div>
                      )
                    )}
                    {gameStarted && (
                      <>
                        <div className={`correct-overlay ${showCorrectOverlay ? 'show' : ''}`}>
                          {streak > 0 && streak % 5 === 0 ? (
                            `${streak} IN A ROW!`
                          ) : (
                            'CORRECT!'
                          )}
                        </div>
                        <div className={`pass-overlay ${showPassOverlay ? 'show' : ''}`}>
                          MISS!
                        </div>
                      </>
                    )}
                  </div>

                  {gameStarted && !showCountdown && (
                    <div className="options-container">
                      {options.map((option, index) => (
                        <button
                          key={option}
                          className={`option-button
                                    color-${(index % 4) + 1}
                                    ${selectedOption === option && option === correctOption ? 'correct-option' : ''}
                                    ${selectedOption === option && option !== correctOption ? 'incorrect-option' : ''}
                                    ${revealedCorrectOption === option ? 'revealed-correct-option' : ''}
                                `}
                          onClick={() => handleOptionClick(option)}
                          disabled={selectedOption !== null || showRoundEndOverlay}
                        >
                          {option}
                        </button>
                      ))}
                      {showReportOptions && (
                        <div className="report-options">
                          <button
                            onClick={() => handleReportClick('Inappropriate')}
                            className="report-option-button-1"
                          >
                            Inappropriate
                          </button>
                          <button
                            onClick={() => handleReportClick('Unusable')}
                            className="report-option-button-2"
                          >
                            Unusable
                          </button>
                        </div>
                      )}
                      <button onClick={toggleReportOptions} className="report-main-button">
                        <FaFlag className="report-icon" />
                      </button>
                    </div>
                  )
                  }
                </>
              )}
            </>
          )}
        </div>
      </div>
      <div className="ad-content-landscape">
        <div className="ad-container-landscape">
          {/* <img src="https://placehold.co/90x90?text=Ads" alt="Placeholder Image 1"></img> */}
          {/* <img src="https://placehold.co/90x90?text=Ad+2" alt="Placeholder Image 2"></img>
          <img src="https://placehold.co/90x90?text=Ad+3" alt="Placeholder Image 3"></img> */}
        </div>
      </div>
      <div className="ad-content-portrait">
        {/* <img src="https://placehold.co/195x40?text=Advertisement" alt="Placeholder Image 1"></img> */}
      </div>

    </div>
  );
}
